import type { QuestionnaireAutomationDraft } from '@/lib/preVisitQuestionnaireTiming';
import { SERVICE_DURATION_MAX } from '@/lib/serviceDuration';
import type {
  BillingMode,
  BookingTiming,
  IntervalUnit,
  RemainingStepsBookableBy,
  TreatmentCoursePractitionerMode,
  TreatmentCourseQuestionnaireMode,
  TreatmentCourseResourceMode,
  TreatmentCourseResourceRequirementDraft,
  TreatmentCourseTemplateDraft,
  TreatmentCourseTemplateReplacePayload,
  TreatmentCourseTemplateStepDraft,
} from '@/types/treatmentCourses';
import {
  BILLING_MODES,
  BOOKING_TIMINGS,
  INTERVAL_UNITS,
  PRACTITIONER_MODES,
  QUESTIONNAIRE_MODES,
  REMAINING_STEPS_BOOKABLE_BY,
  RESOURCE_MODES,
} from '@/types/treatmentCourses';

export const DEFAULT_TREATMENT_COURSE_COLOR = '#0F766E';

export type TreatmentCourseTemplatePayloadError =
  | 'name_required'
  | 'steps_required'
  | 'service_required';

function newClientId(): string {
  return crypto.randomUUID();
}

function isOneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asFiniteInt(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return Math.trunc(n);
  }
  return fallback;
}

function asNullableInt(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = asFiniteInt(value, Number.NaN);
  return Number.isFinite(n) ? n : null;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

function clampInterval(value: number): number {
  return Math.max(0, Math.min(5200, value));
}

function clampDuration(value: number | null): number | null {
  if (value == null) return null;
  if (value <= 0) return null;
  return Math.max(1, Math.min(SERVICE_DURATION_MAX, value));
}

export function createEmptyStepDraft(isFirst: boolean): TreatmentCourseTemplateStepDraft {
  return {
    clientId: newClientId(),
    service_id: '',
    title: '',
    interval_value: isFirst ? 0 : 1,
    interval_unit: isFirst ? 'days' : 'weeks',
    interval_tolerance_value: 0,
    interval_tolerance_unit: 'days',
    duration_minutes: null,
    practitioner_mode: 'inherit',
    practitioner_ids: [],
    resource_mode: 'inherit',
    resource_requirements: [],
    questionnaire_mode: 'inherit',
    automations: [],
    allowed_forms_template_ids: [],
  };
}

export function createEmptyTemplateDraft(): TreatmentCourseTemplateDraft {
  return {
    catalog_service_id: null,
    name: '',
    description: '',
    color: DEFAULT_TREATMENT_COURSE_COLOR,
    is_active: true,
    sort_order: 0,
    booking_timing: 'all_upfront',
    remaining_steps_bookable_by: 'staff_and_patient',
    billing_mode: 'per_step',
    package_price_cents: null,
    steps: [createEmptyStepDraft(true)],
  };
}

function parseAutomation(value: unknown, index: number): QuestionnaireAutomationDraft | null {
  const row = asRecord(value);
  if (!row) return null;
  const formsTemplateId = asString(row.forms_template_id).trim();
  const phase = row.phase;
  if (!formsTemplateId) return null;
  if (phase !== 'before_appointment' && phase !== 'after_appointment') return null;
  return {
    id: asString(row.id) || newClientId(),
    forms_template_id: formsTemplateId,
    phase,
    offset_minutes: Math.max(0, asFiniteInt(row.offset_minutes, 0)),
    delay_after_booking_minutes: Math.max(0, asFiniteInt(row.delay_after_booking_minutes, 0)),
    is_active: asBoolean(row.is_active, true),
    sort_order: asFiniteInt(row.sort_order, index),
  };
}

function parseResourceRequirement(value: unknown): TreatmentCourseResourceRequirementDraft | null {
  const row = asRecord(value);
  if (!row) return null;
  const resourceGroupId = asString(row.resource_group_id);
  if (!resourceGroupId) return null;
  const eligibility = row.eligibility_mode === 'selected' ? 'selected' : 'any';
  return {
    resource_group_id: resourceGroupId,
    quantity: Math.max(1, Math.min(100, asFiniteInt(row.quantity, 1))),
    eligibility_mode: eligibility,
    eligible_resource_ids: asStringArray(row.resource_ids ?? row.eligible_resource_ids),
  };
}

function parseStep(value: unknown, index: number): TreatmentCourseTemplateStepDraft | null {
  const row = asRecord(value);
  if (!row) return null;
  const automations = Array.isArray(row.automations)
    ? row.automations
        .map((item, automationIndex) => parseAutomation(item, automationIndex))
        .filter((item): item is QuestionnaireAutomationDraft => item != null)
    : [];
  const requirements = Array.isArray(row.resource_requirements)
    ? row.resource_requirements
        .map(parseResourceRequirement)
        .filter((item): item is TreatmentCourseResourceRequirementDraft => item != null)
    : [];
  const isFirst = index === 0;
  const parsedSort = asNullableInt(row.sort_order);
  return {
    clientId: asString(row.id) || newClientId(),
    ...(parsedSort != null && parsedSort >= 1 ? { sort_order: parsedSort } : {}),
    service_id: asString(row.service_id),
    title: asString(row.title),
    interval_value: isFirst ? 0 : clampInterval(asFiniteInt(row.interval_value, 0)),
    interval_unit: isOneOf<IntervalUnit>(row.interval_unit, INTERVAL_UNITS, 'days'),
    interval_tolerance_value: isFirst
      ? 0
      : clampInterval(asFiniteInt(row.interval_tolerance_value, 0)),
    interval_tolerance_unit: isOneOf<IntervalUnit>(
      row.interval_tolerance_unit,
      INTERVAL_UNITS,
      'days',
    ),
    duration_minutes: clampDuration(asNullableInt(row.duration_minutes)),
    practitioner_mode: isOneOf<TreatmentCoursePractitionerMode>(
      row.practitioner_mode,
      PRACTITIONER_MODES,
      'inherit',
    ),
    practitioner_ids: asStringArray(row.practitioner_ids),
    resource_mode: isOneOf<TreatmentCourseResourceMode>(
      row.resource_mode,
      RESOURCE_MODES,
      'inherit',
    ),
    resource_requirements: requirements,
    questionnaire_mode: isOneOf<TreatmentCourseQuestionnaireMode>(
      row.questionnaire_mode,
      QUESTIONNAIRE_MODES,
      'inherit',
    ),
    automations,
    allowed_forms_template_ids: [...new Set(asStringArray(row.allowed_forms_template_ids))],
  };
}

export function parseTreatmentCourseTemplate(value: unknown): TreatmentCourseTemplateDraft | null {
  const row = asRecord(value);
  if (!row) return null;
  const name = asString(row.name).trim();
  if (!name) return null;
  const rawSteps = Array.isArray(row.steps) ? row.steps : [];
  const steps = rawSteps
    .map((step, index) => parseStep(step, index))
    .filter((step): step is TreatmentCourseTemplateStepDraft => step != null);
  return {
    id: asString(row.id) || undefined,
    catalog_service_id: asString(row.catalog_service_id) || null,
    name,
    description: asString(row.description),
    color: asString(row.color, DEFAULT_TREATMENT_COURSE_COLOR) || DEFAULT_TREATMENT_COURSE_COLOR,
    is_active: asBoolean(row.is_active, true),
    sort_order: asFiniteInt(row.sort_order, 0),
    booking_timing: isOneOf<BookingTiming>(row.booking_timing, BOOKING_TIMINGS, 'all_upfront'),
    remaining_steps_bookable_by: isOneOf<RemainingStepsBookableBy>(
      row.remaining_steps_bookable_by,
      REMAINING_STEPS_BOOKABLE_BY,
      'staff_and_patient',
    ),
    billing_mode: isOneOf<BillingMode>(row.billing_mode, BILLING_MODES, 'per_step'),
    package_price_cents: asNullableInt(row.package_price_cents),
    steps: steps.length > 0 ? steps : [createEmptyStepDraft(true)],
  };
}

export function parseTreatmentCourseTemplateList(value: unknown): TreatmentCourseTemplateDraft[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(parseTreatmentCourseTemplate)
    .filter((row): row is TreatmentCourseTemplateDraft => row != null);
}

export function validateTreatmentCourseTemplateDraft(
  draft: TreatmentCourseTemplateDraft,
): TreatmentCourseTemplatePayloadError | null {
  if (!draft.name.trim()) return 'name_required';
  if (draft.steps.length < 1) return 'steps_required';
  if (draft.steps.some((step) => !step.service_id)) return 'service_required';
  return null;
}

export function buildTreatmentCourseTemplatePayload(
  draft: TreatmentCourseTemplateDraft,
): TreatmentCourseTemplateReplacePayload {
  const packagePrice =
    draft.billing_mode === 'package' ? Math.max(0, draft.package_price_cents ?? 0) : null;

  return {
    ...(draft.id ? { id: draft.id } : {}),
    ...(draft.catalog_service_id ? { catalog_service_id: draft.catalog_service_id } : {}),
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    color: draft.color || DEFAULT_TREATMENT_COURSE_COLOR,
    is_active: draft.is_active,
    sort_order: draft.sort_order,
    booking_timing: draft.booking_timing,
    remaining_steps_bookable_by: draft.remaining_steps_bookable_by,
    billing_mode: draft.billing_mode,
    package_price_cents: packagePrice,
    steps: draft.steps.map((step, index) => {
      const isFirst = index === 0;
      const questionnaireMode = step.questionnaire_mode;
      const automations =
        questionnaireMode === 'inherit'
          ? []
          : step.automations
              .filter((row) => row.forms_template_id)
              .map((row, automationIndex) => ({
                forms_template_id: row.forms_template_id,
                phase: row.phase,
                offset_minutes: row.offset_minutes,
                delay_after_booking_minutes: 0,
                is_active: row.is_active,
                sort_order: row.sort_order || automationIndex,
              }));

      return {
        sort_order: index + 1,
        service_id: step.service_id,
        title: step.title.trim() || null,
        interval_value: isFirst ? 0 : clampInterval(step.interval_value),
        interval_unit: isFirst ? 'days' : step.interval_unit,
        interval_tolerance_value: isFirst ? 0 : clampInterval(step.interval_tolerance_value),
        interval_tolerance_unit: isFirst ? 'days' : step.interval_tolerance_unit,
        duration_minutes: clampDuration(step.duration_minutes),
        practitioner_mode: step.practitioner_mode,
        practitioner_ids:
          step.practitioner_mode === 'selected' ? [...new Set(step.practitioner_ids)] : [],
        resource_mode: step.resource_mode,
        resource_requirements:
          step.resource_mode === 'override'
            ? step.resource_requirements
                .filter((req) => req.resource_group_id)
                .map((req) => ({
                  resource_group_id: req.resource_group_id,
                  quantity: Math.max(1, Math.min(100, req.quantity || 1)),
                  eligibility_mode: req.eligibility_mode,
                  resource_ids:
                    req.eligibility_mode === 'selected' ? [...req.eligible_resource_ids] : [],
                }))
            : [],
        questionnaire_mode: questionnaireMode,
        automations,
        allowed_forms_template_ids: [
          ...new Set(step.allowed_forms_template_ids.filter((id) => id.trim().length > 0)),
        ].sort(),
      };
    }),
  };
}

export function cloneTemplateDraft(
  template: TreatmentCourseTemplateDraft,
): TreatmentCourseTemplateDraft {
  return {
    ...template,
    description: template.description ?? '',
    steps: template.steps.map((step) => ({
      ...step,
      practitioner_ids: [...step.practitioner_ids],
      resource_requirements: step.resource_requirements.map((req) => ({
        ...req,
        eligible_resource_ids: [...req.eligible_resource_ids],
      })),
      automations: step.automations.map((row) => ({ ...row })),
      allowed_forms_template_ids: [...step.allowed_forms_template_ids],
    })),
  };
}

export function moveTemplateStep(
  steps: TreatmentCourseTemplateStepDraft[],
  fromIndex: number,
  toIndex: number,
): TreatmentCourseTemplateStepDraft[] {
  if (fromIndex === toIndex) return steps;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= steps.length || toIndex >= steps.length) {
    return steps;
  }
  const next = [...steps];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next.map((step, index) =>
    index === 0
      ? { ...step, interval_value: 0, interval_tolerance_value: 0, interval_unit: 'days' }
      : step,
  );
}
